/**
 * The only permitted window global in this package, wrapped once.
 *
 * `03-ENGINEERING-SEAMS.md` §1.1 exempts `packages/ui/src/layout/**` for exactly
 * two globals — `matchMedia` and `ResizeObserver` — because a container-width
 * hook and an environment branch cannot exist without them and there is no port
 * that could carry them into a presentational package. Everything else in
 * `@ff/ui` still goes through a port, so this file keeps the exemption to one
 * readable place rather than letting `matchMedia` appear in five hooks.
 *
 * Observation only: nothing here writes to the document or to an element.
 */

import { useMemo, useSyncExternalStore } from "react";

export type MatchMedia = (query: string) => MediaQueryList;

/**
 * `null` rather than a throw or a stub: a non-browser render (a test that never
 * installed a `matchMedia`, or a future prerender) has no environment to read,
 * and the correct answer there is "no override applies", not a crash.
 */
export function matchMediaOrNull(): MatchMedia | null {
  const candidate: unknown = (globalThis as { matchMedia?: unknown }).matchMedia;
  if (typeof candidate !== "function") return null;
  return (query: string) => (candidate as MatchMedia).call(globalThis, query);
}

export interface ExternalStore<T> {
  subscribe: (onStoreChange: () => void) => () => void;
  getSnapshot: () => T;
}

/**
 * What a probe learned, and which queries have to change before that answer
 * could change.
 *
 * The queries are part of the result because some probes are derived: the
 * accessibility-text-size probe measures the viewport first and only then knows
 * which `em` query to watch, so the subscription set is not knowable up front.
 */
export interface MediaProbeResult<T> {
  readonly value: T;
  readonly queries: readonly string[];
}

function listen(list: MediaQueryList, onChange: () => void): () => void {
  if (typeof list.addEventListener === "function") {
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }
  // Safari before 14 exposes only the deprecated pair. Dropping it would make
  // the accessibility branch silently non-reactive on those versions rather
  // than merely unsupported.
  list.addListener(onChange);
  return () => list.removeListener(onChange);
}

/**
 * A `useSyncExternalStore` source over one or more media queries.
 *
 * The snapshot is cached and recomputed only on a `change` event, because
 * `getSnapshot` runs on every render and the accessibility probe costs a dozen
 * `matchMedia` calls. Subscriptions are rebuilt after every change for the same
 * reason the probe returns its queries: a derived probe watches a different set
 * once the environment moves.
 */
export function createMediaProbeStore<T>(
  probe: (matchMedia: MatchMedia) => MediaProbeResult<T>,
  fallback: T,
): ExternalStore<T> {
  let snapshot: T | undefined;
  let queries: readonly string[] = [];
  const listeners = new Set<() => void>();
  let detach: (() => void) | null = null;

  function evaluate(): void {
    const matchMedia = matchMediaOrNull();
    if (matchMedia === null) {
      snapshot = fallback;
      queries = [];
      return;
    }
    const result = probe(matchMedia);
    snapshot = result.value;
    queries = result.queries;
  }

  function attach(): void {
    const matchMedia = matchMediaOrNull();
    if (matchMedia === null || queries.length === 0) return;
    const removers = queries.map((query) => listen(matchMedia(query), handleChange));
    detach = () => {
      for (const remove of removers) remove();
    };
  }

  function handleChange(): void {
    detach?.();
    detach = null;
    evaluate();
    attach();
    for (const listener of listeners) listener();
  }

  return {
    getSnapshot(): T {
      if (snapshot === undefined) evaluate();
      // `snapshot` is assigned by `evaluate`; the cast avoids widening T with
      // `undefined` purely to describe a one-time lazy initialisation.
      return snapshot as T;
    },
    subscribe(onStoreChange: () => void): () => void {
      listeners.add(onStoreChange);
      if (listeners.size === 1) {
        if (snapshot === undefined) evaluate();
        attach();
      }
      return () => {
        listeners.delete(onStoreChange);
        if (listeners.size === 0) {
          detach?.();
          detach = null;
        }
      };
    },
  };
}

/** A store over a single boolean media query. */
export function createMediaQueryStore(query: string): ExternalStore<boolean> {
  return createMediaProbeStore(
    (matchMedia) => ({ value: matchMedia(query).matches, queries: [query] }),
    false,
  );
}

/**
 * One store per consuming component rather than a module-level singleton: a
 * singleton would keep a snapshot taken under one environment alive across a
 * test that installs a different one, and the whole point of these hooks is
 * that a test can pin the environment.
 */
export function useMediaProbe<T>(
  probe: (matchMedia: MatchMedia) => MediaProbeResult<T>,
  fallback: T,
): T {
  const store = useMemo(() => createMediaProbeStore(probe, fallback), [probe, fallback]);
  return useSyncExternalStore(store.subscribe, store.getSnapshot, () => fallback);
}
